import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { CartItem } from '@/pages/home/page';
import { useTheme } from '@/hooks/useTheme';

interface HotProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  thumbnail: string;
  images: string[];
  description: string;
  sizes: string[];
  colors?: { name: string; hex: string; price?: number }[];
  stock: number;
}

interface HotCollectionProps {
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
}

export default function HotCollection({ onAddToCart }: HotCollectionProps) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [products, setProducts] = useState<HotProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Fetch trending + approved products from Supabase
  useEffect(() => {
    const fetchTrending = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('products')
          .select('id, name, category, price, original_price, thumbnail, images, description, sizes, colors, stock')
          .eq('status', 'approved')
          .eq('is_trending', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('HotCollection Supabase error:', error.message, error.details);
        }
        if (!error && data) {
          setProducts(data.map((p: Record<string, unknown>) => ({
            id: p.id as string,
            name: p.name as string,
            category: p.category as string,
            price: p.price as number,
            originalPrice: (p.original_price as number) || (p.price as number),
            thumbnail: (p.thumbnail as string) || '',
            images: (p.images as string[]) || [],
            description: (p.description as string) || '',
            sizes: (p.sizes as string[]) || [],
            colors: p.colors as { name: string; hex: string; price?: number }[] | undefined,
            stock: (p.stock as number) || 0,
          })));
        }
      } catch (e) {
        console.error('HotCollection fetch error:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchTrending();

    // Real-time subscription for trending changes
    const sub = supabase
      .channel('trending-products')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, () => {
        fetchTrending();
      })
      .subscribe();

    return () => { supabase.removeChannel(sub); };
  }, []);

  // Auto-scroll every 2.8s
  const scrollToIndex = useCallback((idx: number) => {
    if (!scrollRef.current || products.length === 0) return;
    const clamped = ((idx % products.length) + products.length) % products.length;
    const itemWidth = scrollRef.current.offsetWidth / getVisibleCount();
    scrollRef.current.scrollTo({ left: clamped * itemWidth, behavior: 'smooth' });
    setCurrentIndex(clamped);
  }, [products.length]);

  function getVisibleCount() {
    if (typeof window === 'undefined') return 2;
    if (window.innerWidth < 480) return 2;
    return 3;
  }

  useEffect(() => {
    if (products.length <= 1 || isPaused) return;
    autoScrollRef.current = setInterval(() => {
      setCurrentIndex(prev => {
        const next = (prev + 1) % products.length;
        if (scrollRef.current) {
          const itemWidth = scrollRef.current.offsetWidth / getVisibleCount();
          scrollRef.current.scrollTo({ left: next * itemWidth, behavior: 'smooth' });
        }
        return next;
      });
    }, 2800);
    return () => { if (autoScrollRef.current) clearInterval(autoScrollRef.current); };
  }, [products.length, isPaused]);

  // onAddToCart prop kept for interface compatibility but not used
  void onAddToCart;

  if (loading) {
    return (
      <section className="py-10 px-4 dark:bg-slate-900">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-6 h-6 rounded-full bg-orange-100 animate-pulse"></div>
            <div className="h-6 w-40 bg-gray-100 rounded-full animate-pulse"></div>
          </div>
          <div className="flex gap-3">
            {[1, 2].map(i => (
              <div key={i} className="flex-shrink-0 w-[calc(50%-6px)] h-64 bg-gray-100 rounded-2xl animate-pulse"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Jab koi trending product nahi - section hide karo
  if (!loading && products.length === 0) return null;

  return (
    <section className="py-10 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-end justify-between mb-5">
          <div>
            <div 
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-2"
              style={{ 
                backgroundColor: `${colors.primary}15`,
                borderColor: `${colors.primary}30`,
                color: colors.primary 
              }}
            >
              <span 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: colors.primary }}
              ></span>
              <span className="text-xs font-bold uppercase tracking-wider">Live on Store</span>
            </div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white leading-tight">
              Hot <span 
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
              >Collection</span>
            </h2>
            <p className="text-gray-400 dark:text-gray-500 text-xs mt-0.5">{products.length} trending products right now</p>
          </div>
          {/* Auto-scroll pause toggle */}
          <button
            onClick={() => setIsPaused(p => !p)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 text-xs font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className={isPaused ? 'ri-play-fill' : 'ri-pause-fill'}></i>
            {isPaused ? 'Play' : 'Pause'}
          </button>
        </div>

        {/* Scroll area */}
        <div className="relative">
          <div
            ref={scrollRef}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setTimeout(() => setIsPaused(false), 3000)}
            onScroll={() => {
              if (!scrollRef.current) return;
              const visCount = getVisibleCount();
              const itemW = scrollRef.current.offsetWidth / visCount;
              setCurrentIndex(Math.round(scrollRef.current.scrollLeft / itemW));
            }}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {products.map((product) => {
              const discount = product.originalPrice > product.price
                ? Math.round((1 - product.price / product.originalPrice) * 100) : 0;
              const image = product.thumbnail || product.images[0] || '';

              return (
                <div
                  key={product.id}
                  className="flex-shrink-0 snap-start hc-card-3d group"
                  style={{ width: 'calc(50% - 6px)' }}
                >
                  {/* Image */}
                  <div className="relative w-full overflow-hidden" style={{ height: '200px', borderRadius: '16px 16px 0 0', background: 'linear-gradient(160deg, #fff7ed 0%, #fff1f2 100%)' }}>
                    {image ? (
                      <img
                        src={image}
                        alt={product.name}
                        className="w-full h-full object-cover object-top hc-img"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-t-shirt-line text-4xl text-gray-200"></i>
                      </div>
                    )}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                         style={{ background: 'linear-gradient(to top, rgba(255,80,0,0.12) 0%, transparent 60%)' }}></div>
                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-black text-white"
                            style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, backdropFilter: 'blur(8px)', boxShadow: `0 2px 10px ${colors.primary}40` }}>
                        🔥 HOT
                      </span>
                      {discount > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black text-white"
                              style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', boxShadow: '0 2px 8px rgba(34,197,94,0.3)' }}>
                          {discount}% OFF
                        </span>
                      )}
                    </div>
                    {product.stock <= 5 && product.stock > 0 && (
                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-black text-white"
                           style={{ background: 'rgba(245,158,11,0.9)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(245,158,11,0.35)' }}>
                        Only {product.stock} left!
                      </div>
                    )}
                  </div>

                  {/* Info — glassmorphism */}
                  <div className="p-3 hc-info-panel" style={{ borderRadius: '0 0 16px 16px' }}>
                    <p className="text-[10px] uppercase tracking-widest font-black"
                       style={{ color: '#f97316' }}>{product.category}</p>
                    <p className="text-gray-900 font-black text-sm leading-tight mt-0.5 line-clamp-2">{product.name}</p>
                    <div className="flex items-center gap-1.5 mt-2 px-2 py-1.5 rounded-xl"
                         style={{ background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.12)' }}>
                      <span className="text-gray-900 font-black text-base">₹{product.price}</span>
                      {product.originalPrice > product.price && (
                        <span className="text-gray-400 text-xs line-through">₹{product.originalPrice}</span>
                      )}
                      {discount > 0 && (
                        <span className="ml-auto text-[10px] font-black px-1.5 py-0.5 rounded-full whitespace-nowrap"
                              style={{ background: 'rgba(249,115,22,0.1)', color: '#ea580c', border: '1px solid rgba(249,115,22,0.18)' }}>
                          Save ₹{product.originalPrice - product.price}
                        </span>
                      )}
                    </div>
                    {/* CTA — View Details only */}
                    <button
                      onClick={() => navigate(`/category?cat=${encodeURIComponent(product.category)}&product=${product.id}`)}
                      className="mt-2.5 w-full py-2.5 rounded-xl text-xs font-black transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 active:scale-95 hc-btn-cart"
                    >
                      <i className="ri-eye-line"></i>View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Left/Right arrows */}
          {products.length > 2 && (
            <>
              <button
                onClick={() => scrollToIndex(currentIndex - 1)}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-8 h-8 rounded-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 flex items-center justify-center text-gray-600 dark:text-gray-300 cursor-pointer z-10"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button
                onClick={() => scrollToIndex(currentIndex + 1)}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-8 h-8 rounded-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 flex items-center justify-center text-gray-600 dark:text-gray-300 cursor-pointer z-10"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </>
          )}
        </div>

        {/* Dot indicators */}
        {products.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-4">
            {products.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToIndex(i)}
                className={`rounded-full transition-all cursor-pointer ${
                  i === currentIndex ? 'w-5 h-2 bg-orange-500' : 'w-2 h-2 bg-gray-200 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        .hc-card-3d {
          border-radius: 18px;
          background: linear-gradient(145deg, #ffffff 0%, #fffaf7 100%);
          border: 1px solid rgba(249,115,22,0.1);
          box-shadow: 0 2px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04);
          transition: transform 0.35s cubic-bezier(0.23,1,0.32,1), box-shadow 0.35s ease;
          overflow: hidden;
          position: relative;
        }
        .hc-card-3d:hover {
          transform: translateY(-7px) scale(1.018) rotateX(2deg);
          box-shadow: 0 18px 44px rgba(249,115,22,0.18), 0 6px 14px rgba(0,0,0,0.09);
        }
        .hc-card-3d::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(120deg, transparent 25%, rgba(255,255,255,0.65) 50%, transparent 75%);
          background-size: 200% 100%;
          pointer-events: none;
          z-index: 5;
          opacity: 0;
          border-radius: inherit;
        }
        .hc-card-3d:hover::before {
          opacity: 1;
          animation: hcShine 0.65s ease forwards;
        }
        @keyframes hcShine {
          from { background-position: -200% center; }
          to   { background-position: 200% center; }
        }
        .hc-img {
          transition: transform 0.6s cubic-bezier(0.23,1,0.32,1);
        }
        .hc-card-3d:hover .hc-img {
          transform: scale(1.09);
        }
        .hc-info-panel {
          background: linear-gradient(to bottom, rgba(255,255,255,0.98), rgba(255,250,245,0.99));
          border-top: 1px solid rgba(249,115,22,0.1);
        }
        .hc-btn-cart {
          background: linear-gradient(135deg, #1a1a2e, #16213e);
          color: white;
          box-shadow: 0 3px 12px rgba(0,0,0,0.2);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hc-btn-cart:hover {
          transform: scale(1.03);
          box-shadow: 0 5px 16px rgba(0,0,0,0.28);
        }
        .hc-btn-added {
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: white;
          box-shadow: 0 3px 12px rgba(34,197,94,0.35);
        }
      `}</style>
    </section>
  );
}
